import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Loader } from '@givebutter/primitives/loader';
import { FetchTasksParams, fetchTaskIds } from 'api/tasks';
import * as Drawer from 'components/drawer';
import { FilterProvider } from 'components/filter/filters-next/FilterProvider';
import { HeaderButton } from 'components/header/HeaderButton';
import { NucleoIcon } from 'components/nucleo-icon';
import { AddTaskKeyboardShortcut } from 'components/tasks/AddTaskKeyboardShortcut';
import { Tasks } from 'components/tasks/list/Tasks';
import { SingleTask } from 'components/tasks/single/SingleTask';
import { useTaskDrawerState } from 'components/tasks/useTaskDrawerState';
import { injectStatusFilter, injectUnarchivedFilter } from 'components/tasks/utils/upstream';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useQueryParams } from 'customHooks/useQueryParams';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twJoin } from 'tailwind-merge';

const TasksDrawer = () => {
    const { accountId, hasGivebutterPlusAccess } = useCurrentAccount();
    const location = useLocation();
    const history = useHistory();
    const { openTasks: openTasksParam } = useQueryParams<{ openTasks: string }>();
    const { selectedTaskId, setSelectedTaskId, isDrawerOpen, setIsDrawerOpen } =
        useTaskDrawerState();
    const TriggerButton = isDrawerOpen ? Drawer.Cancel : Drawer.Trigger;

    const taskCountParams: FetchTasksParams = {
        filters: [...injectStatusFilter(['todo', 'in_progress']), ...injectUnarchivedFilter()],
    };

    const { data: taskCount } = useSuspenseQuery({
        queryKey: accountQueryKeys.taskIdsWithFilters(accountId, taskCountParams),
        queryFn: () => fetchTaskIds(accountId, taskCountParams),
        select: data => data.length,
        staleTime: 0,
    });

    if (typeof openTasksParam !== 'undefined') {
        return <Redirect to={location.pathname} />;
    }

    if (typeof openTasksParam !== 'undefined') {
        return <Redirect to={location.pathname} />;
    }

    return (
        <FilterProvider cacheKey={'tasks'} resource={'tasks'}>
            <Drawer.Root
                modal={false}
                open={isDrawerOpen}
                onOpenChange={isOpen => {
                    if (hasGivebutterPlusAccess) {
                        setIsDrawerOpen(isOpen);
                        if (!isOpen && selectedTaskId) setSelectedTaskId(null);
                    } else {
                        history.push(`/accounts/${accountId}/tasks`);
                    }
                }}
            >
                <TriggerButton asChild>
                    <HeaderButton
                        className={twJoin(isDrawerOpen && 'bg-opacity-20')}
                        data-intercom-target={IntercomProductTourTargets.HeaderTasks}
                    >
                        <div className="relative">
                            {taskCount > 0 && (
                                <span className="absolute -right-0.5 top-0.5 size-1.5 rounded-full bg-red-600" />
                            )}
                            <NucleoIcon icon="tasks" />
                        </div>
                        Tasks
                    </HeaderButton>
                </TriggerButton>
                <Drawer.Content
                    className="top-16 flex h-[calc(100vh-4rem)] flex-col border-l border-gray-200 bg-white"
                    overlayClassName="hidden"
                    onEscapeKeyDown={e => {
                        if (selectedTaskId) {
                            e.preventDefault();
                            setSelectedTaskId(null);
                        }
                    }}
                >
                    <Suspense fallback={<Loader />}>
                        <ErrorBoundary
                            fallback={<Redirect to={location.pathname} />}
                            onError={() => setSelectedTaskId(null)}
                        >
                            {selectedTaskId ? <SingleTask key={selectedTaskId} /> : <Tasks />}
                        </ErrorBoundary>
                    </Suspense>
                </Drawer.Content>
            </Drawer.Root>
            <AddTaskKeyboardShortcut />
        </FilterProvider>
    );
};

export { TasksDrawer };
